import { useEffect, type RefObject } from "react";

/**
 * Adds iOS-style rubber-band overscroll to the nearest scrollable ancestor.
 * Skips on iOS/Safari where native rubber-banding already exists.
 */
export function useRubberBand(ref: RefObject<HTMLElement | null>) {
	useEffect(() => {
		// iOS/Safari already has native rubber-banding
		if (/iPad|iPhone|iPod/.test(navigator.userAgent) || CSS.supports("-webkit-touch-callout", "none")) {
			return;
		}

		const el = ref.current;
		if (!el) return;

		// Find the nearest scrollable ancestor
		const scrollParent = findScrollParent(el);
		if (!scrollParent) return;

		// The element we'll transform (the first child / content wrapper)
		const content = scrollParent.firstElementChild as HTMLElement | null;
		if (!content) return;

		content.style.willChange = "transform";

		let startY = 0;
		let startScrollTop = 0;
		let currentOffset = 0;
		let pulling = false;
		let rafId = 0;

		// Dampening curve — diminishing returns like iOS
		// offset * (1 - 1/(|offset|*k/height + 1))... simplified to a power curve
		function dampen(raw: number): number {
			const height = scrollParent!.clientHeight;
			const max = height * 0.4;
			const sign = raw < 0 ? -1 : 1;
			const abs = Math.min(Math.abs(raw), height);
			// Strong diminishing resistance
			return sign * max * (1 - Math.exp(-abs / (height * 0.35)));
		}

		function applyTransform(offset: number) {
			currentOffset = offset;
			content!.style.transform = offset === 0 ? "" : `translateY(${offset}px)`;
		}

		function handleTouchStart(e: TouchEvent) {
			// Cancel any ongoing spring animation
			if (rafId) {
				cancelAnimationFrame(rafId);
				rafId = 0;
			}
			// Snap back if mid-animation
			if (currentOffset !== 0) {
				applyTransform(0);
				content!.style.transition = "";
			}
			startY = e.touches[0].clientY;
			startScrollTop = scrollParent!.scrollTop;
			pulling = false;
		}

		function handleTouchMove(e: TouchEvent) {
			const dy = e.touches[0].clientY - startY;
			const atTop = scrollParent!.scrollTop <= 0 && dy > 0;
			const atBottom =
				scrollParent!.scrollTop >= scrollParent!.scrollHeight - scrollParent!.clientHeight - 1 &&
				dy < 0;

			if (atTop || atBottom) {
				// How far past the boundary
				let overscroll: number;
				if (atTop) {
					overscroll = dy - (startScrollTop - scrollParent!.scrollTop);
					overscroll = Math.max(0, overscroll);
				} else {
					const maxScroll = scrollParent!.scrollHeight - scrollParent!.clientHeight;
					overscroll = dy + (maxScroll - startScrollTop);
					overscroll = Math.min(0, overscroll);
				}

				if (overscroll !== 0) {
					pulling = true;
					e.preventDefault();
					applyTransform(dampen(overscroll));
				}
			} else if (pulling) {
				// User scrolled back into normal range
				pulling = false;
				snapBack();
			}
		}

		function handleTouchEnd() {
			if (pulling || currentOffset !== 0) {
				pulling = false;
				snapBack();
			}
		}

		function snapBack() {
			// Use CSS transition for spring-like bounce back
			content!.style.transition = "transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 1.2)";
			applyTransform(0);

			// Clean up transition after it ends
			const onEnd = () => {
				content!.style.transition = "";
				content!.removeEventListener("transitionend", onEnd);
			};
			content!.addEventListener("transitionend", onEnd);
		}

		scrollParent.addEventListener("touchstart", handleTouchStart, { passive: true });
		scrollParent.addEventListener("touchmove", handleTouchMove, { passive: false });
		scrollParent.addEventListener("touchend", handleTouchEnd, { passive: true });

		return () => {
			scrollParent.removeEventListener("touchstart", handleTouchStart);
			scrollParent.removeEventListener("touchmove", handleTouchMove);
			scrollParent.removeEventListener("touchend", handleTouchEnd);
			if (rafId) cancelAnimationFrame(rafId);
			content!.style.willChange = "";
			content!.style.transform = "";
			content!.style.transition = "";
		};
	}, [ref]);
}

function findScrollParent(el: HTMLElement): HTMLElement | null {
	let node: HTMLElement | null = el.parentElement;
	while (node) {
		const style = getComputedStyle(node);
		if (
			/(auto|scroll)/.test(style.overflowY) &&
			node.scrollHeight > node.clientHeight
		) {
			return node;
		}
		node = node.parentElement;
	}
	return null;
}
